// WebAuthn helpers for Rebels admin passkeys (add-rebels-admin-security-and-mcp-connectors, R4; port of the QPortal file).
// The server sends options and expects credentials in the WebAuthn Level 3 JSON form (binary fields as base64url).
const toBase64Url = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const fromBase64Url = (value) => {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
};
export const passkeysSupported = () => typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials?.create;
const parseCreation = (json) => {
    const options = JSON.parse(json);
    const native = PublicKeyCredential.parseCreationOptionsFromJSON;
    if (typeof native === 'function')
        return native(options);
    return {
        ...options,
        challenge: fromBase64Url(options.challenge),
        user: { ...options.user, id: fromBase64Url(options.user.id) },
        excludeCredentials: (options.excludeCredentials || []).map((c) => ({ ...c, id: fromBase64Url(c.id) }))
    };
};
const parseRequest = (json) => {
    const options = JSON.parse(json);
    const native = PublicKeyCredential.parseRequestOptionsFromJSON;
    if (typeof native === 'function')
        return native(options);
    return {
        ...options,
        challenge: fromBase64Url(options.challenge),
        allowCredentials: (options.allowCredentials || []).map((c) => ({ ...c, id: fromBase64Url(c.id) }))
    };
};
const serialize = (credential) => {
    if (typeof credential.toJSON === 'function')
        return JSON.stringify(credential.toJSON());
    const response = credential.response;
    const json = {
        id: credential.id,
        rawId: toBase64Url(credential.rawId),
        type: credential.type,
        authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
        clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
        response: { clientDataJSON: toBase64Url(response.clientDataJSON) }
    };
    if (response.attestationObject) {
        json.response.attestationObject = toBase64Url(response.attestationObject);
        json.response.transports = response.getTransports?.() ?? [];
    }
    else {
        json.response.authenticatorData = toBase64Url(response.authenticatorData);
        json.response.signature = toBase64Url(response.signature);
        if (response.userHandle)
            json.response.userHandle = toBase64Url(response.userHandle);
    }
    return JSON.stringify(json);
};
export class PasskeyCancelled extends Error {
}
const run = async (operation) => {
    try {
        const credential = await operation();
        if (!credential)
            throw new PasskeyCancelled('No passkey was selected.');
        return serialize(credential);
    }
    catch (error) {
        if (error instanceof PasskeyCancelled || error?.name === 'NotAllowedError' || error?.name === 'AbortError') {
            throw new PasskeyCancelled('The passkey request was cancelled or timed out.');
        }
        throw error;
    }
};
export const createPasskey = (optionsJson) => run(() => navigator.credentials.create({ publicKey: parseCreation(optionsJson) }));
export const getPasskey = (optionsJson) => run(() => navigator.credentials.get({ publicKey: parseRequest(optionsJson) }));
